import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  signInWithPhoneNumber,
  RecaptchaVerifier,
  onAuthStateChanged,
  signOut,
  User,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { auth, recordPhoneAuthSuccess } from "../utils/eira/firebase";
import TrustSignalsManager from "../utils/eira/TrustSignalsManager";
import RecaptchaVerifierManager from "../utils/eira/RecaptchaVerifierManager";
import tokenService from "../services/tokenService";
import { getClinicUserId } from "../utils/eira/helper";

// Enhanced error types
interface AuthErrorDetails {
  code: string;
  message: string;
  userFriendlyMessage: string;
  retryable: boolean;
  suggestedAction?: string;
}

// Enhanced authentication state
interface IEnhancedAppContext {
  // Basic state
  initializingApp: boolean;
  setInitializingApp: (value: boolean) => void;
  user: User | null;
  clinicUserId: string | null;
  setClinicUserId: (value: string | null) => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
  containerId: string;
  setContainerId: (value: string) => void;
  isConfirmationReady: boolean;

  // Enhanced authentication methods
  otpLogin: (
    phoneNumber: string,
    appVerifier: RecaptchaVerifier
  ) => Promise<any>;
  verifyOtp: (otp: string) => Promise<any>;
  logout: () => Promise<void>;

  // Enhanced error handling
  lastError: AuthErrorDetails | null;
  clearError: () => void;
  retryLastOperation: () => Promise<void>;

  // Trust signals integration
  trustScore: number;
  riskLevel: "low" | "medium" | "high";
  authAttempts: number;
  canRetry: boolean;

  // Performance monitoring
  authPerformanceMetrics: {
    otpSendTime?: number;
    otpVerifyTime?: number;
    recaptchaSetupTime?: number;
    lastAuthDuration?: number;
  };

  // Enhanced status tracking
  authStatus:
    | "idle"
    | "sending_otp"
    | "verifying_otp"
    | "setting_up_recaptcha"
    | "success"
    | "error";
  recaptchaStatus:
    | "not_initialized"
    | "initializing"
    | "ready"
    | "error"
    | "expired";
}

export const enhancedAppContext = createContext<IEnhancedAppContext>(
  {} as IEnhancedAppContext
);

const useEnhancedAuthenticationProvider = () => {
  // Basic state
  const [initializingApp, setInitializingApp] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const [clinicUserId, setClinicUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [containerId, setContainerId] = useState<string>("");
  const [isConfirmationReady, setIsConfirmationReady] =
    useState<boolean>(false);

  // Enhanced state
  const [lastError, setLastError] = useState<AuthErrorDetails | null>(null);
  const [authAttempts, setAuthAttempts] = useState<number>(0);
  const [trustScore, setTrustScore] = useState<number>(0);
  const [riskLevel, setRiskLevel] = useState<"low" | "medium" | "high">(
    "medium"
  );
  const [authStatus, setAuthStatus] = useState<
    | "idle"
    | "sending_otp"
    | "verifying_otp"
    | "setting_up_recaptcha"
    | "success"
    | "error"
  >("idle");
  const [recaptchaStatus, setRecaptchaStatus] = useState<
    "not_initialized" | "initializing" | "ready" | "error" | "expired"
  >("not_initialized");
  const [authPerformanceMetrics, setAuthPerformanceMetrics] = useState<{
    otpSendTime?: number;
    otpVerifyTime?: number;
    recaptchaSetupTime?: number;
    lastAuthDuration?: number;
  }>({});

  // Refs for tracking operations
  const lastOperationRef = useRef<(() => Promise<void>) | null>(null);
  const currentPhoneNumberRef = useRef<string>("");
  const performanceTimersRef = useRef<Map<string, number>>(new Map());
  const hasExchangedToken = useRef<boolean>(false);

  /**
   * Enhanced error parsing with user-friendly messages
   */
  const parseAuthError = useCallback((error: any): AuthErrorDetails => {
    const code = error.code || "unknown";
    const message = error.message || "Unknown error occurred";

    let userFriendlyMessage = message;
    let retryable = true;
    let suggestedAction: string | undefined;

    switch (code) {
      case "auth/invalid-phone-number":
        userFriendlyMessage =
          "The phone number format is invalid. Please check and try again.";
        retryable = true;
        suggestedAction = "Verify your phone number format";
        break;

      case "auth/too-many-requests":
        userFriendlyMessage =
          "Too many attempts. Please wait a few minutes before trying again.";
        retryable = false;
        suggestedAction = "Wait 5-10 minutes before retrying";
        break;

      case "auth/invalid-verification-code":
        userFriendlyMessage =
          "The verification code is incorrect. Please try again.";
        retryable = true;
        suggestedAction = "Check the SMS and enter the correct code";
        break;

      case "auth/code-expired":
        userFriendlyMessage =
          "The verification code has expired. Please request a new one.";
        retryable = true;
        suggestedAction = "Request a new verification code";
        break;

      case "auth/session-expired":
        userFriendlyMessage = "Your session has expired. Please start over.";
        retryable = true;
        suggestedAction = "Start the login process again";
        break;

      case "auth/quota-exceeded":
        userFriendlyMessage = "SMS quota exceeded. Please try again later.";
        retryable = false;
        suggestedAction = "Try again in a few hours";
        break;

      case "auth/captcha-check-failed":
      case "auth/recaptcha-not-enabled":
        userFriendlyMessage = "Security verification failed. Please try again.";
        retryable = true;
        suggestedAction = "Complete the security verification";
        break;

      case "auth/network-request-failed":
        userFriendlyMessage =
          "Network error. Please check your connection and try again.";
        retryable = true;
        suggestedAction = "Check your internet connection";
        break;

      default:
        if (message.includes("timeout")) {
          userFriendlyMessage = "Request timed out. Please try again.";
          suggestedAction = "Check your connection and retry";
        } else if (message.includes("reCAPTCHA")) {
          userFriendlyMessage =
            "Security verification is required. Please complete the challenge.";
          suggestedAction = "Complete the reCAPTCHA verification";
        }
        break;
    }

    return {
      code,
      message,
      userFriendlyMessage,
      retryable,
      suggestedAction,
    };
  }, []);

  /**
   * Start performance timer
   */
  const startTimer = useCallback((operation: string) => {
    performanceTimersRef.current.set(operation, Date.now());
  }, []);

  /**
   * End performance timer and update metrics
   */
  const endTimer = useCallback((operation: string) => {
    const startTime = performanceTimersRef.current.get(operation);
    if (startTime) {
      const duration = Date.now() - startTime;
      setAuthPerformanceMetrics((prev) => ({
        ...prev,
        [`${operation}Time`]: duration,
      }));
      performanceTimersRef.current.delete(operation);
      return duration;
    }
    return 0;
  }, []);

  /**
   * Update trust signals and calculate risk level
   */
  const updateTrustSignals = useCallback(() => {
    try {
      const signals = TrustSignalsManager.getTrustSignalsForRecaptcha();
      setTrustScore(signals.trustScore);
      setRiskLevel(signals.riskLevel);

      localStorage.setItem(
        "current_trust_score",
        signals.trustScore.toString()
      );
      localStorage.setItem("current_risk_level", signals.riskLevel);

      return signals;
    } catch (error) {
      console.warn("Failed to update trust signals:", error);
      return null;
    }
  }, []);

  /**
   * Exchange Firebase token for backend token
   */
  const exchangeTokenWithBackend = useCallback(
    async (firebaseUser: User) => {
      if (hasExchangedToken.current) {
        return;
      }

      try {
        hasExchangedToken.current = true;

        const idToken = await firebaseUser.getIdToken();

        // Call the fixed token service method
        const tokenResponse = await tokenService.exchangeFirebaseToken(idToken);

        if (tokenResponse && tokenResponse.success) {
          const phoneNumber = firebaseUser.phoneNumber;
          
          // Validate phone number exists
          if (!phoneNumber) {
            console.error("No phone number found in Firebase user");
            throw new Error("Phone number not found in user profile");
          }
          
          const clinicUserId = getClinicUserId(phoneNumber);
          
          // Validate clinic user ID was generated successfully
          if (!clinicUserId) {
            console.error("Failed to generate clinic user ID from phone number:", phoneNumber);
            throw new Error("Failed to generate user ID from phone number");
          }

          // Verify token was actually saved by checking IndexedDB
          const savedToken = await tokenService.getStoredToken();
          if (!savedToken) {
            console.error(" Token was not saved to IndexedDB");
            throw new Error("Token storage failed");
          }

          setClinicUserId(clinicUserId);
          setUser(firebaseUser);
          setAuthStatus("success");
          setAuthAttempts(0);

          // Record successful authentication
          if (phoneNumber) {
            recordPhoneAuthSuccess(phoneNumber);
            TrustSignalsManager.recordSuccessfulAuth("phone", {
              phoneNumber,
            });
          }
        } else {
          console.error(" Token exchange failed - invalid response");
          throw new Error(
            "Token exchange failed - invalid response from server"
          );
        }
      } catch (error) {
        console.error(" Token exchange failed:", error);
        hasExchangedToken.current = false;
        setAuthStatus("error");
        setLastError(parseAuthError(error));

        // Clear any partially stored data
        await tokenService.clearStoredToken();
        throw error;
      }
    },
    [parseAuthError]
  );

  /**
   * Enhanced container ID initialization
   */
  useEffect(() => {
    try {
      const storedContainerId = sessionStorage.getItem("recaptcha_container");
      const deviceType = /iPhone|iPad|iPod|Mac/.test(navigator.userAgent)
        ? "apple"
        : "standard";
      const timestamp = Date.now();

      const newContainerId =
        storedContainerId || `recaptcha-${deviceType}-${timestamp}`;

      if (!storedContainerId) {
        sessionStorage.setItem("recaptcha_container", newContainerId);
      }

      setContainerId(newContainerId);
      updateTrustSignals();
    } catch (e) {
      console.warn("Could not initialize container ID:", e);
      setContainerId(`recaptcha-fallback-${Date.now()}`);
    }
  }, [updateTrustSignals]);

  /**
   * Check stored token on app initialization
   */
  useEffect(() => {
    const checkStoredAuth = async () => {
      try {
        const isValidToken = await tokenService.isTokenValid();
        const storedUser = await tokenService.getStoredUser();
        const storedClinicUserId = await tokenService.getClinicUserId();

        if (isValidToken && storedUser && storedClinicUserId) {
          setClinicUserId(storedClinicUserId);
          setAuthStatus("success");
          hasExchangedToken.current = true;
          setInitializingApp(false);
          return true;
        } else {
          if (!isValidToken) {
            await tokenService.clearStoredToken();
          }
        }
      } catch (error) {
        console.error("Error checking stored auth:", error);
        await tokenService.clearStoredToken();
      }
      setInitializingApp(false);
      return false;
    };

    // Check stored auth first
    checkStoredAuth();
  }, []);

  /**
   * Enhanced authentication state listener
   */
  useEffect(() => {
    const setupAuthListener = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);

        const unsubscribe = onAuthStateChanged(auth, async (loggedInUser) => {
          if (loggedInUser && !hasExchangedToken.current) {
            try {
              await exchangeTokenWithBackend(loggedInUser);
            } catch (error) {
              console.error(" Failed to exchange token:", error);
              // Don't clear the user immediately, let them try again
            }
          } else if (!loggedInUser) {
            // Only clear state if we don't have a valid stored token
            const hasValidToken = await tokenService.isTokenValid();
            if (!hasValidToken) {
              setUser(null);
              setClinicUserId(null);
              hasExchangedToken.current = false;
              if (authStatus !== "idle") {
                setAuthStatus("idle");
              }
            } else {
            }
          } else if (loggedInUser && hasExchangedToken.current) {
          }
        });

        return unsubscribe;
      } catch (error) {
        console.error("Failed to setup auth listener:", error);
      }
    };

    setupAuthListener();
  }, [exchangeTokenWithBackend, authStatus]);

  /**
   * Enhanced OTP login with comprehensive error handling and performance monitoring
   */
  const otpLogin = useCallback(
    async (
      phoneNumber: string,
      appVerifier: RecaptchaVerifier
    ): Promise<any> => {
      try {
        setAuthStatus("sending_otp");
        setLoading(true);
        setLastError(null);
        startTimer("otpSend");

        updateTrustSignals();

        if (!phoneNumber || !phoneNumber.match(/^\+\d{10,15}$/)) {
          throw new Error("Invalid phone number format");
        }

        const formattedPhone = phoneNumber.startsWith("+")
          ? phoneNumber
          : `+${phoneNumber}`;
        currentPhoneNumberRef.current = formattedPhone;

        lastOperationRef.current = () => otpLogin(phoneNumber, appVerifier);

        try {
          localStorage.setItem(
            "last_phone_prefix",
            formattedPhone.substring(0, 4)
          );
          localStorage.setItem("last_phone_suffix", formattedPhone.slice(-2));
          localStorage.setItem("otp_send_attempt", new Date().toISOString());
          localStorage.setItem("current_risk_level", riskLevel);
        } catch (storageError) {
          console.warn("Could not save verification state:", storageError);
        }

        const confirmation = await signInWithPhoneNumber(
          auth,
          formattedPhone,
          appVerifier
        );

        const sendDuration = endTimer("otpSend");

        try {
          const otpCount =
            parseInt(localStorage.getItem("otp_send_count") || "0") + 1;
          localStorage.setItem("otp_send_count", otpCount.toString());
          localStorage.setItem(
            "last_otp_send_success",
            new Date().toISOString()
          );
          localStorage.setItem("otp_send_duration", sendDuration.toString());
        } catch (e) {
          console.warn("Could not update OTP send metrics:", e);
        }

        setConfirmationResult(confirmation);
        setIsConfirmationReady(true);
        setAuthStatus("idle");

        return confirmation;
      } catch (error: any) {
        endTimer("otpSend");
        const authError = parseAuthError(error);
        setLastError(authError);
        setAuthStatus("error");

        const newAttempts = authAttempts + 1;
        setAuthAttempts(newAttempts);

        TrustSignalsManager.recordFailedAuth("phone", authError.code);

        try {
          localStorage.setItem(
            "last_otp_send_error",
            JSON.stringify({
              ...authError,
              timestamp: new Date().toISOString(),
              attempts: newAttempts,
              trustScore,
              riskLevel,
            })
          );
        } catch (e) {
          console.warn("Could not store error details:", e);
        }

        console.error(" OTP send failed:", authError);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [
      authAttempts,
      riskLevel,
      trustScore,
      parseAuthError,
      startTimer,
      endTimer,
      updateTrustSignals,
    ]
  );

  /**
   * Enhanced OTP verification with performance monitoring
   */
  const verifyOtp = useCallback(
    async (otp: string): Promise<any> => {
      if (!confirmationResult) {
        const error = new Error("No confirmation result available");
        setLastError(parseAuthError(error));
        throw error;
      }

      if (!otp || otp.length !== 6) {
        const error = new Error("Please enter a valid 6-digit OTP");
        setLastError(parseAuthError(error));
        throw error;
      }

      try {
        setAuthStatus("verifying_otp");
        setLoading(true);
        setLastError(null);
        startTimer("otpVerify");

        lastOperationRef.current = () => verifyOtp(otp);

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error("OTP verification timed out")),
            45000
          )
        );

        const result = await Promise.race([
          confirmationResult.confirm(otp),
          timeoutPromise,
        ]);

        if (result.user) {
          const verifyDuration = endTimer("otpVerify");
          const totalDuration =
            (authPerformanceMetrics.otpSendTime || 0) + verifyDuration;

          setAuthPerformanceMetrics((prev) => ({
            ...prev,
            lastAuthDuration: totalDuration,
          }));

          const phoneNumber =
            result.user.phoneNumber || currentPhoneNumberRef.current;

          if (phoneNumber) {
            recordPhoneAuthSuccess(phoneNumber);
            TrustSignalsManager.recordSuccessfulAuth("phone", {
              phoneNumber,
              verificationDuration: verifyDuration,
              totalDuration,
            });
          }

          try {
            localStorage.setItem(
              "last_successful_login",
              new Date().toISOString()
            );
            localStorage.setItem(
              "otp_verify_duration",
              verifyDuration.toString()
            );
            localStorage.setItem(
              "total_auth_duration",
              totalDuration.toString()
            );

            const verifyCount =
              parseInt(localStorage.getItem("otp_verify_count") || "0") + 1;
            localStorage.setItem("otp_verify_count", verifyCount.toString());
            localStorage.setItem("auth_phone_verified", "true");
          } catch (e) {
            console.warn("Could not update verification metrics:", e);
          }

          setTimeout(() => {
            setConfirmationResult(null);
            setIsConfirmationReady(false);
          }, 100);

          // Note: Token exchange will happen in onAuthStateChanged listener
          return result;
        } else {
          throw new Error("Authentication failed - no user returned");
        }
      } catch (error: any) {
        endTimer("otpVerify");
        const authError = parseAuthError(error);
        setLastError(authError);
        setAuthStatus("error");

        if (error.message?.includes("timed out")) {
          setConfirmationResult(null);
          setIsConfirmationReady(false);
          authError.userFriendlyMessage =
            "Verification timed out. Please request a new OTP and try again.";
          authError.suggestedAction = "Request a new OTP";
        }

        TrustSignalsManager.recordFailedAuth("phone_verify", authError.code);

        const newAttempts = authAttempts + 1;
        setAuthAttempts(newAttempts);

        console.error(" OTP verification failed:", authError);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [
      confirmationResult,
      authAttempts,
      authPerformanceMetrics.otpSendTime,
      parseAuthError,
      startTimer,
      endTimer,
    ]
  );

  /**
   * Enhanced logout with cleanup
   */
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      setAuthStatus("idle");

      await signOut(auth);
      await tokenService.clearStoredToken();

      setUser(null);
      setClinicUserId(null);
      setConfirmationResult(null);
      setIsConfirmationReady(false);
      setLastError(null);
      setAuthAttempts(0);
      hasExchangedToken.current = false;

      try {
        sessionStorage.removeItem("hasNavigatedBefore");
        sessionStorage.removeItem("recaptcha_container");
      } catch (error) {
        console.warn("Could not clear session data:", error);
      }

      RecaptchaVerifierManager.clearAllRecaptchas();
      setRecaptchaStatus("not_initialized");
    } catch (error) {
      console.error(" Error during logout:", error);
      const authError = parseAuthError(error);
      setLastError(authError);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [parseAuthError]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setLastError(null);
    if (authStatus === "error") {
      setAuthStatus("idle");
    }
  }, [authStatus]);

  /**
   * Retry last operation
   */
  const retryLastOperation = useCallback(async (): Promise<void> => {
    if (lastOperationRef.current && lastError?.retryable) {
      clearError();
      setAuthAttempts((prev) => prev + 1);
      await lastOperationRef.current();
    }
  }, [lastError?.retryable, clearError]);

  /**
   * Calculate if retry is allowed
   */
  const canRetry = Boolean(
    lastOperationRef.current && lastError?.retryable && authAttempts < 5
  );

  return {
    // Basic properties
    initializingApp,
    setInitializingApp,
    user,
    otpLogin,
    verifyOtp,
    logout,
    clinicUserId,
    setClinicUserId,
    loading,
    setLoading,
    containerId,
    setContainerId,
    isConfirmationReady,

    // Enhanced properties
    lastError,
    clearError,
    retryLastOperation,
    trustScore,
    riskLevel,
    authAttempts,
    canRetry,
    authPerformanceMetrics,
    authStatus,
    recaptchaStatus,
  };
};

interface Props {
  children: React.ReactNode;
}

export const EnhancedAuthenticationProvider = ({ children }: Props) => {
  const app = useEnhancedAuthenticationProvider();

  return (
    <enhancedAppContext.Provider value={app}>
      {children}
    </enhancedAppContext.Provider>
  );
};

export const useEnhancedAuth = () => {
  return useContext(enhancedAppContext);
};

// Backward compatibility export
export const authenticateApp = useEnhancedAuth;
